import type {
  TTypographySettingsCssVars,
  TypographySettings,
} from '../../types'
import { typeSafeObjectEntries } from '../../util/typeSafeObjectEntries'
import { mediaQueryForLayoutSizeLGOrLarger } from '../components/Layout'

type TypeVariants =
  | 'textS'
  | 'textSBold'
  | 'textM'
  | 'textMBold'
  | 'textL'
  | 'textLBold'
  | 'displayCapsuleS'
  | 'displayCapsuleM'
  | 'displayCapsuleL'
  | 'displayNibS'
  | 'displayNibM'
  | 'displayNibL'
  | 'bookCoverNibS'
  | 'bookCoverNibM'
  | 'bookCoverNibL'
  | 'bookCoverCapsuleS'
  | 'bookCoverCapsuleM'
  | 'bookCoverCapsuleL'

export type TTypePalette = { [variant in TypeVariants]: TypographySettings }

export type TypeStyleName = keyof TTypePalette

const textFontStack = `"Capsule Sans Text", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif`
const displayCapsuleFontStack = `"Capsule Sans Display", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif`
const displayNibFontStack = `"Nib Pro Display", Georgia, serif`
const bookCoverCapsuleFontStack = `"Capsule Sans Book Cover", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif`
const bookCoverNibFontStack = `"Nib Pro Book Cover", Georgia, serif`

export type TTypePaletteCssVarNames = {
  [K in keyof TTypePalette]: TTypographySettingsCssVars
}

type TTypePaletteResponsiveTypographySettingsOverride = Partial<{
  [K in keyof TTypePalette]: Partial<TypographySettings>
}>

const TypePalette: TTypePalette = {
  bookCoverCapsuleL: {
    fontSize: '72px',
    fontWeight: 400,
    letterSpacing: '-2px',
    lineHeight: 1.22,
    stack: bookCoverCapsuleFontStack,
  },
  bookCoverCapsuleM: {
    fontSize: '56px',
    fontWeight: 400,
    letterSpacing: '-1.33px',
    lineHeight: 1.22,
    stack: bookCoverCapsuleFontStack,
  },
  bookCoverCapsuleS: {
    fontSize: '44px',
    fontWeight: 400,
    letterSpacing: '-1.33px',
    lineHeight: 1.22,
    stack: bookCoverCapsuleFontStack,
  },
  bookCoverNibL: {
    fontSize: '72px',
    fontWeight: 300,
    letterSpacing: '-2px',
    lineHeight: 1.22,
    stack: bookCoverNibFontStack,
  },
  bookCoverNibM: {
    fontSize: '56px',
    fontWeight: 300,
    letterSpacing: '-1.33px',
    lineHeight: 1.22,
    stack: bookCoverNibFontStack,
  },
  bookCoverNibS: {
    fontSize: '44px',
    fontWeight: 300,
    letterSpacing: '-1px',
    lineHeight: 1.22,
    stack: bookCoverNibFontStack,
  },
  displayCapsuleL: {
    fontSize: '32px',
    fontWeight: 500,
    letterSpacing: '-0.33px',
    lineHeight: 1.25,
    stack: displayCapsuleFontStack,
  },
  displayCapsuleM: {
    fontSize: '24px',
    fontWeight: 500,
    letterSpacing: '-0.1px',
    lineHeight: 1.35,
    stack: displayCapsuleFontStack,
  },
  displayCapsuleS: {
    fontSize: '21px',
    fontWeight: 500,
    letterSpacing: '-0.1px',
    lineHeight: 1.35,
    stack: displayCapsuleFontStack,
  },
  displayNibL: {
    fontSize: '32px',
    fontWeight: 300,
    letterSpacing: '-0.33px',
    lineHeight: 1.25,
    stack: displayNibFontStack,
  },
  displayNibM: {
    fontSize: '24px',
    fontWeight: 400,
    letterSpacing: '-0.33px',
    lineHeight: 1.35,
    stack: displayNibFontStack,
  },
  displayNibS: {
    fontSize: '21px',
    fontWeight: 400,
    letterSpacing: '-0.1px',
    lineHeight: 1.35,
    stack: displayNibFontStack,
  },
  textL: {
    fontSize: '18px',
    fontWeight: 400,
    letterSpacing: '-0.25px',
    lineHeight: 1.53,
    stack: textFontStack,
  },
  textLBold: {
    fontSize: '18px',
    fontWeight: 700,
    letterSpacing: '-0.25px',
    lineHeight: 1.53,
    stack: textFontStack,
  },
  textM: {
    fontSize: '15px',
    fontWeight: 400,
    letterSpacing: '-0.1px',
    lineHeight: 1.6,
    stack: textFontStack,
  },
  textMBold: {
    fontSize: '15px',
    fontWeight: 700,
    letterSpacing: '-0.1px',
    lineHeight: 1.6,
    stack: textFontStack,
  },
  textS: {
    fontSize: '13px',
    fontWeight: 400,
    letterSpacing: '-0.1px',
    lineHeight: 1.53,
    stack: textFontStack,
  },
  textSBold: {
    fontSize: '13px',
    fontWeight: 700,
    letterSpacing: '-0.1px',
    lineHeight: 1.53,
    stack: textFontStack,
  },
}

export const TypePaletteCssVarNames: TTypePaletteCssVarNames = {
  bookCoverCapsuleL: {
    fontSize: '--rh__type-palette__bookCoverCapsuleL__font-size',
    fontWeight: '--rh__type-palette__bookCoverCapsuleL__font-weight',
    letterSpacing: '--rh__type-palette__bookCoverCapsuleL__letter-spacing',
    lineHeight: '--rh__type-palette__bookCoverCapsuleL__line-height',
    stack: '--rh__type-palette__bookCoverCapsuleL__stack',
  },
  bookCoverCapsuleM: {
    fontSize: '--rh__type-palette__bookCoverCapsuleM__font-size',
    fontWeight: '--rh__type-palette__bookCoverCapsuleM__font-weight',
    letterSpacing: '--rh__type-palette__bookCoverCapsuleM__letter-spacing',
    lineHeight: '--rh__type-palette__bookCoverCapsuleM__line-height',
    stack: '--rh__type-palette__bookCoverCapsuleM__stack',
  },
  bookCoverCapsuleS: {
    fontSize: '--rh__type-palette__bookCoverCapsuleS__font-size',
    fontWeight: '--rh__type-palette__bookCoverCapsuleS__font-weight',
    letterSpacing: '--rh__type-palette__bookCoverCapsuleS__letter-spacing',
    lineHeight: '--rh__type-palette__bookCoverCapsuleS__line-height',
    stack: '--rh__type-palette__bookCoverCapsuleS__stack',
  },
  bookCoverNibL: {
    fontSize: '--rh__type-palette__bookCoverNibL__font-size',
    fontWeight: '--rh__type-palette__bookCoverNibL__font-weight',
    letterSpacing: '--rh__type-palette__bookCoverNibL__letter-spacing',
    lineHeight: '--rh__type-palette__bookCoverNibL__line-height',
    stack: '--rh__type-palette__bookCoverNibL__stack',
  },
  bookCoverNibM: {
    fontSize: '--rh__type-palette__bookCoverNibM__font-size',
    fontWeight: '--rh__type-palette__bookCoverNibM__font-weight',
    letterSpacing: '--rh__type-palette__bookCoverNibM__letter-spacing',
    lineHeight: '--rh__type-palette__bookCoverNibM__line-height',
    stack: '--rh__type-palette__bookCoverNibM__stack',
  },
  bookCoverNibS: {
    fontSize: '--rh__type-palette__bookCoverNibS__font-size',
    fontWeight: '--rh__type-palette__bookCoverNibS__font-weight',
    letterSpacing: '--rh__type-palette__bookCoverNibS__letter-spacing',
    lineHeight: '--rh__type-palette__bookCoverNibS__line-height',
    stack: '--rh__type-palette__bookCoverNibS__stack',
  },
  displayCapsuleL: {
    fontSize: '--rh__type-palette__displayCapsuleL__font-size',
    fontWeight: '--rh__type-palette__displayCapsuleL__font-weight',
    letterSpacing: '--rh__type-palette__displayCapsuleL__letter-spacing',
    lineHeight: '--rh__type-palette__displayCapsuleL__line-height',
    stack: '--rh__type-palette__displayCapsuleL__stack',
  },
  displayCapsuleM: {
    fontSize: '--rh__type-palette__displayCapsuleM__font-size',
    fontWeight: '--rh__type-palette__displayCapsuleM__font-weight',
    letterSpacing: '--rh__type-palette__displayCapsuleM__letter-spacing',
    lineHeight: '--rh__type-palette__displayCapsuleM__line-height',
    stack: '--rh__type-palette__displayCapsuleM__stack',
  },
  displayCapsuleS: {
    fontSize: '--rh__type-palette__displayCapsuleS__font-size',
    fontWeight: '--rh__type-palette__displayCapsuleS__font-weight',
    letterSpacing: '--rh__type-palette__displayCapsuleS__letter-spacing',
    lineHeight: '--rh__type-palette__displayCapsuleS__line-height',
    stack: '--rh__type-palette__displayCapsuleS__stack',
  },
  displayNibL: {
    fontSize: '--rh__type-palette__displayNibL__font-size',
    fontWeight: '--rh__type-palette__displayNibL__font-weight',
    letterSpacing: '--rh__type-palette__displayNibL__letter-spacing',
    lineHeight: '--rh__type-palette__displayNibL__line-height',
    stack: '--rh__type-palette__displayNibL__stack',
  },
  displayNibM: {
    fontSize: '--rh__type-palette__displayNibM__font-size',
    fontWeight: '--rh__type-palette__displayNibM__font-weight',
    letterSpacing: '--rh__type-palette__displayNibM__letter-spacing',
    lineHeight: '--rh__type-palette__displayNibM__line-height',
    stack: '--rh__type-palette__displayNibM__stack',
  },
  displayNibS: {
    fontSize: '--rh__type-palette__displayNibS__font-size',
    fontWeight: '--rh__type-palette__displayNibS__font-weight',
    letterSpacing: '--rh__type-palette__displayNibS__letter-spacing',
    lineHeight: '--rh__type-palette__displayNibS__line-height',
    stack: '--rh__type-palette__displayNibS__stack',
  },
  textL: {
    fontSize: '--rh__type-palette__textL__font-size',
    fontWeight: '--rh__type-palette__textL__font-weight',
    letterSpacing: '--rh__type-palette__textL__letter-spacing',
    lineHeight: '--rh__type-palette__textL__line-height',
    stack: '--rh__type-palette__textL__stack',
  },
  textLBold: {
    fontSize: '--rh__type-palette__textLBold__font-size',
    fontWeight: '--rh__type-palette__textLBold__font-weight',
    letterSpacing: '--rh__type-palette__textLBold__letter-spacing',
    lineHeight: '--rh__type-palette__textLBold__line-height',
    stack: '--rh__type-palette__textLBold__stack',
  },
  textM: {
    fontSize: '--rh__type-palette__textM__font-size',
    fontWeight: '--rh__type-palette__textM__font-weight',
    letterSpacing: '--rh__type-palette__textM__letter-spacing',
    lineHeight: '--rh__type-palette__textM__line-height',
    stack: '--rh__type-palette__textM__stack',
  },
  textMBold: {
    fontSize: '--rh__type-palette__textMBold__font-size',
    fontWeight: '--rh__type-palette__textMBold__font-weight',
    letterSpacing: '--rh__type-palette__textMBold__letter-spacing',
    lineHeight: '--rh__type-palette__textMBold__line-height',
    stack: '--rh__type-palette__textMBold__stack',
  },
  textS: {
    fontSize: '--rh__type-palette__textS__font-size',
    fontWeight: '--rh__type-palette__textS__font-weight',
    letterSpacing: '--rh__type-palette__textS__letter-spacing',
    lineHeight: '--rh__type-palette__textS__line-height',
    stack: '--rh__type-palette__textS__stack',
  },
  textSBold: {
    fontSize: '--rh__type-palette__textSBold__font-size',
    fontWeight: '--rh__type-palette__textSBold__font-weight',
    letterSpacing: '--rh__type-palette__textSBold__letter-spacing',
    lineHeight: '--rh__type-palette__textSBold__line-height',
    stack: '--rh__type-palette__textSBold__stack',
  },
}

export const TypePaletteResponsiveOverrides: {
  [mediaQuery: string]: TTypePaletteResponsiveTypographySettingsOverride
} = {
  [mediaQueryForLayoutSizeLGOrLarger]: {
    bookCoverCapsuleM: {
      fontSize: '64px',
      letterSpacing: '-1.33px',
      lineHeight: 1.25,
    },
    bookCoverNibM: {
      fontSize: '64px',
      letterSpacing: '-1.33px',
      lineHeight: 1.25,
    },
  },
}

export function getInitialTypographyCssVars() {
  return getTypographyCssVarsAndValues(TypePaletteCssVarNames, TypePalette)
}

export function getResponsiveTypographyCssVars() {
  return typeSafeObjectEntries(TypePaletteResponsiveOverrides).reduce(
    (accumulator, [mediaQuery, responsiveOverride]) =>
      Object.assign(accumulator, {
        [mediaQuery]: getTypographyCssVarsAndValues(
          TypePaletteCssVarNames,
          responsiveOverride
        ),
      }),
    {}
  )
}

function getTypographyCssVarsAndValues(
  names: TTypePaletteCssVarNames,
  palette: TTypePalette | TTypePaletteResponsiveTypographySettingsOverride
) {
  const variables: { [key: string]: string | number } = {}
  typeSafeObjectEntries(names).forEach(([typeStyleKey, variableNames]) => {
    typeSafeObjectEntries(variableNames).forEach(
      ([typeSettingKey, variableName]) => {
        const value = palette[typeStyleKey]?.[typeSettingKey]
        if (variableName !== undefined && value !== undefined) {
          variables[variableName] = value
        }
      }
    )
  })
  return variables
}
